import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { KineticLines, Reveal, SectionHead } from "@/components/Motion";
import CTASection from "@/components/CTASection";
import Marquee from "@/components/Marquee";
import { IMAGES } from "@/config/images";

const CAPS = [
  { name: "Fibre Laser Cutting", img: IMAGES.laser, desc: "High-precision cutting of stainless steel and sheet metal with clean edges and minimal heat distortion — the starting point of accurate fabrication." },
  { name: "CNC Press Brake Forming", img: IMAGES.cnc, desc: "Controlled bending and forming of sheet components to drawing dimensions, with repeatable results across batches." },
  { name: "Laser & TIG Welding", img: IMAGES.laser, desc: "Smooth, continuous welds suited to cleanroom equipment — minimizing crevices and supporting cleanability." },
  { name: "Stainless Steel Fabrication", img: IMAGES.passbox, desc: "SS 304 stainless steel fabrication with cleanroom-compatible finishes, from pass box chambers to furniture and enclosures." },
  { name: "Assembly & Finishing", img: IMAGES.iot, desc: "Mechanical assembly, integration of interlocks, blowers, controls and sensors, plus surface finishing to specification." },
  { name: "Custom OEM Manufacturing", img: IMAGES.cnc, desc: "Contract fabrication and OEM manufacturing to customer drawings — panels, chassis, frames, enclosures and assemblies." },
];

export default function Capabilities() {
  return (
    <div data-testid="capabilities-page">
      <SEO title="Manufacturing Capabilities" path="/capabilities" image={IMAGES.laser}
        description="STJ Innovations manufacturing: fibre laser cutting, CNC press brake forming, laser and TIG welding, stainless steel fabrication, assembly, finishing and custom OEM manufacturing in India." />
      <section className="blueprint-dark bg-ink">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Breadcrumbs dark items={[{ label: "Manufacturing Capabilities" }]} />
          <p className="eyebrow eyebrow-light">Manufacturing</p>
          <KineticLines lines={["Built on Modern", "Manufacturing"]}
            className="mt-5 font-display text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl" />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
            Cutting, forming, welding, finishing and assembly of stainless steel and sheet-metal products — engineered for cleanroom and industrial applications.
          </p>
        </div>
      </section>
      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <SectionHead eyebrow="Capabilities" title="Process Capability"
          copy="Our fabrication capability covers the full journey from sheet to finished equipment. Actual machine photographs and detailed equipment specifications are published here as our facility documentation grows — contact our team for current capability details." />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {CAPS.map((c, i) => (
            <Reveal key={c.name} delay={(i % 3) * 0.07}>
              <article className="group border border-line bg-white" data-testid={`capability-card-${i}`}>
                <div className="spotlight-img aspect-[16/9] overflow-hidden bg-navy">
                  <img src={c.img} alt={`${c.name} at STJ Innovations`} loading="lazy"
                    className="h-full w-full object-cover opacity-85 transition-transform duration-700 group-hover:scale-[1.04]" />
                </div>
                <div className="p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-2 font-display text-lg font-semibold text-ink">{c.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-steel">{c.desc}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-line bg-mist">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Work With Us" title="From Drawing to Delivered Product"
            copy="Share your drawings, CAD data or concept. Our team will review manufacturability, recommend materials and finishes, and quote with realistic lead times." />
        </div>
      </section>
      <CTASection />
    </div>
  );
}
